"use client";

import { Component, type ReactNode } from "react";

interface PreviewErrorBoundaryProps {
  /** Rendered when the live-query subtree throws — the static draft. */
  fallback: ReactNode;
  children: ReactNode;
}

interface PreviewErrorBoundaryState {
  hasError: boolean;
}

/**
 * Catches errors thrown by the live-query subtree and degrades to the static
 * draft instead of letting the error escape to Next.js `global-error`
 * ("This page couldn't load").
 *
 * The live-query (`useLiveQuery`) runs in the browser and talks to
 * `api.sanity.io` directly, so it can fail for reasons unrelated to the page:
 * the app's origin missing from the Sanity project's CORS allowlist, an expired
 * token, or a transient network blip. By default such a failure throws during
 * hydration and blanks the whole preview. With this boundary the editor still
 * sees their draft — just without real-time updates — and the failure is logged
 * rather than fatal.
 */
export class PreviewErrorBoundary extends Component<
  PreviewErrorBoundaryProps,
  PreviewErrorBoundaryState
> {
  state: PreviewErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PreviewErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error(
      "[preview] live-query failed; falling back to static draft",
      error,
    );
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
